import React, { useEffect, useState } from 'react';
import {
  Button, Card, Descriptions, Divider, Form, Input, message, Radio,
  Result, Select, Space, Steps, Typography, Upload,
} from 'antd';
import {
  InboxOutlined, MinusCircleOutlined, PlusOutlined, RocketOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { createVersion, getProducts } from '../services/api';
import {
  DAILY_ONLY_PRODUCTS, DAILY_PRODUCTS, getProductDisplayName, isMainVersionProductVisible,
} from '../utils/format';

const { TextArea } = Input;
const { Dragger } = Upload;
const { Text, Title } = Typography;

const versionTypeNames = {
  release: '发布版本',
  daily: '每日版本',
  market: '市场版本',
};

const releaseTypeNames = {
  major: 'Major（重大更新）',
  minor: 'Minor（功能更新）',
  patch: 'Patch（补丁修复）',
};

function isProductSelectableForVersionType(product, versionType) {
  if (!isMainVersionProductVisible(product)) return false;
  if (versionType === 'daily') return DAILY_PRODUCTS.includes(product.model);
  if (versionType === 'release') return !DAILY_ONLY_PRODUCTS.includes(product.model);
  return true;
}

export default function VersionUpload() {
  const [current, setCurrent] = useState(0);
  const [form] = Form.useForm();
  const [products, setProducts] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [features, setFeatures] = useState([{ type: 'new_feature', title: '', description: '' }]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [createdId, setCreatedId] = useState(null);
  const navigate = useNavigate();

  const versionType = Form.useWatch('version_type', form) || 'release';
  const marketProduct = products.find(p => p.model === 'MARKET');
  const isMarketVersion = versionType === 'market';

  useEffect(() => {
    getProducts().then(res => setProducts(res.data.data));
  }, []);

  useEffect(() => {
    if (isMarketVersion) {
      form.setFieldsValue({
        product_id: marketProduct?.id,
        market_category: form.getFieldValue('market_category') || '未分类',
        release_type: form.getFieldValue('release_type') || 'minor',
      });
    } else if (form.getFieldValue('product_id') === marketProduct?.id) {
      form.setFieldsValue({ product_id: undefined });
    } else {
      const productId = form.getFieldValue('product_id');
      const selectedProduct = products.find(p => p.id === productId);
      if (selectedProduct && !isProductSelectableForVersionType(selectedProduct, versionType)) {
        form.setFieldsValue({ product_id: undefined });
      }
    }
  }, [form, isMarketVersion, marketProduct, products, versionType]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (values.version_type === 'market' && fileList.length === 0) {
        message.warning('市场版本必须上传文件');
        return;
      }

      setSubmitting(true);
      const formData = new FormData();
      if (values.product_id) formData.append('product_id', values.product_id);
      formData.append('version', values.version);
      formData.append('release_type', values.release_type || 'minor');
      formData.append('description', values.description || '');
      formData.append('uploaded_by', values.uploaded_by || 'admin');
      formData.append('version_type', values.version_type || 'release');
      if (values.market_category) formData.append('market_category', values.market_category);
      formData.append('features', JSON.stringify(features.filter(f => f.title.trim())));

      if (fileList.length > 0 && fileList[0].originFileObj) {
        formData.append('file', fileList[0].originFileObj);
      }

      const res = await createVersion(formData);
      setCreatedId(res.data.data.id);
      setSuccess(true);
      message.success('版本创建成功！');
    } catch (err) {
      if (err.response?.data?.message) {
        message.error(err.response.data.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const addFeature = () => {
    setFeatures([...features, { type: 'new_feature', title: '', description: '' }]);
  };

  const removeFeature = (index) => {
    setFeatures(features.filter((_, i) => i !== index));
  };

  const updateFeature = (index, field, value) => {
    const updated = [...features];
    updated[index] = { ...updated[index], [field]: value };
    setFeatures(updated);
  };

  const goNext = async () => {
    try {
      if (current === 0) {
        await form.validateFields();
      }
    } catch (err) {
      return;
    }
    if (current === 1 && isMarketVersion && fileList.length === 0) {
      message.warning('市场版本必须上传文件');
      return;
    }
    setCurrent(current + 1);
  };

  if (success) {
    const listPath = form.getFieldValue('version_type') === 'market' ? '/versions/market' : '/versions';
    return (
      <Card className="content-card">
        <Result
          status="success"
          title="版本上传成功！"
          subTitle={`版本 ${form.getFieldValue('version') || ''} 已成功创建`}
          extra={[
            <Button key="list" onClick={() => navigate(listPath)}>返回列表</Button>,
            <Button key="detail" type="primary" onClick={() => navigate(`/versions/${createdId}`)}>查看详情</Button>,
          ]}
        />
      </Card>
    );
  }

  return (
    <div className="page-shell">
      <div className="page-heading">
        <div>
          <div className="page-kicker">Release Workflow</div>
          <Title level={3} className="page-heading-title">版本上传</Title>
          <Text className="page-heading-subtitle">
            按基本信息、文件、功能记录和提交确认四步创建版本；市场版本会归档到 X:\4_市场版本。
          </Text>
        </div>
      </div>

      <Form form={form} layout="vertical" initialValues={{ version_type: 'release', release_type: 'minor', uploaded_by: 'admin' }}>
        <Card className="content-card">
          <Steps current={current} items={[
            { title: '基本信息' },
            { title: '文件上传' },
            { title: '新增功能' },
            { title: '确认提交' },
          ]} style={{ marginBottom: 32 }} />

          <div style={{ minHeight: 300 }}>
            <div style={{ display: current === 0 ? 'block' : 'none', maxWidth: 620, margin: '0 auto' }}>
              <Form.Item name="version_type" label="版本类型">
                <Radio.Group size="large">
                  <Radio.Button value="release">发布版本</Radio.Button>
                  <Radio.Button value="daily">每日版本</Radio.Button>
                  <Radio.Button value="market">市场版本</Radio.Button>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                name="product_id"
                label="产品型号"
                rules={isMarketVersion ? [] : [{ required: true, message: '请选择产品' }]}
              >
                <Select
                  placeholder={isMarketVersion ? '市场版本统一归档' : '请选择产品'}
                  options={isMarketVersion
                    ? [{ label: '市场版本', value: marketProduct?.id }].filter(option => option.value)
                    : products
                      .filter(p => isProductSelectableForVersionType(p, versionType))
                      .map(p => ({ label: getProductDisplayName(p.model), value: p.id }))}
                  disabled={isMarketVersion}
                  size="large"
                />
              </Form.Item>

              {isMarketVersion && (
                <Form.Item
                  name="market_category"
                  label="市场分类 / 客户"
                  rules={[{ required: true, message: '请输入市场分类或客户名称' }]}
                >
                  <Input placeholder="例如：华为、联想平板、未分类" size="large" />
                </Form.Item>
              )}

              <Space style={{ width: '100%' }} size={16} wrap>
                <Form.Item
                  name="version"
                  label="版本号"
                  rules={[{ required: true, message: '请输入版本号' }]}
                  style={{ flex: '1 1 220px' }}
                >
                  <Input placeholder={isMarketVersion ? '例如：36.24.0_P0003' : '例如：3.3.0'} size="large" style={{ fontFamily: 'monospace' }} />
                </Form.Item>
                <Form.Item name="release_type" label="发布类型" style={{ flex: '0 0 180px' }}>
                  <Select size="large" options={[
                    { value: 'major', label: releaseTypeNames.major },
                    { value: 'minor', label: releaseTypeNames.minor },
                    { value: 'patch', label: releaseTypeNames.patch },
                  ]} />
                </Form.Item>
              </Space>

              <Form.Item name="uploaded_by" label="上传者">
                <Input placeholder="上传者姓名" size="large" />
              </Form.Item>
              <Form.Item name="description" label="版本描述">
                <TextArea rows={4} placeholder="请描述此版本的主要变更内容..." />
              </Form.Item>
            </div>

            <div style={{ display: current === 1 ? 'block' : 'none', maxWidth: 620, margin: '0 auto' }}>
              <Dragger
                fileList={fileList}
                onChange={({ fileList: fl }) => setFileList(fl)}
                beforeUpload={() => false}
                maxCount={1}
              >
                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
                <p className="ant-upload-hint">
                  支持固件包、压缩包、安装包和文档等格式，单文件最大 500MB。
                </p>
              </Dragger>
              <div style={{ marginTop: 16, padding: 16, background: '#f6f8fa', borderRadius: 8 }}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {isMarketVersion
                    ? '市场版本必须上传文件，提交后会保存到 X:\\4_市场版本\\分类\\版本号。'
                    : '上传后将自动计算文件 SHA256 哈希值，用于版本完整性校验。如暂无文件可跳过此步骤。'}
                </Text>
              </div>
            </div>

            <div style={{ display: current === 2 ? 'block' : 'none', maxWidth: 700, margin: '0 auto' }}>
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary">记录此版本的新增功能、改进和修复内容</Text>
              </div>
              {features.map((f, index) => (
                <Card key={index} className="content-card" size="small" style={{ marginBottom: 12 }}
                  extra={
                    features.length > 1 && (
                      <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => removeFeature(index)} size="small">
                        移除
                      </Button>
                    )
                  }
                >
                  <Space direction="vertical" style={{ width: '100%' }} size={8}>
                    <Select value={f.type} onChange={v => updateFeature(index, 'type', v)} style={{ width: 140 }} size="small">
                      <Select.Option value="new_feature">新功能</Select.Option>
                      <Select.Option value="improvement">改进</Select.Option>
                      <Select.Option value="bugfix">修复</Select.Option>
                    </Select>
                    <Input
                      placeholder="功能标题"
                      value={f.title}
                      onChange={e => updateFeature(index, 'title', e.target.value)}
                    />
                    <TextArea
                      placeholder="详细描述（选填）"
                      rows={2}
                      value={f.description}
                      onChange={e => updateFeature(index, 'description', e.target.value)}
                    />
                  </Space>
                </Card>
              ))}
              <Button type="dashed" onClick={addFeature} block icon={<PlusOutlined />}>
                添加功能记录
              </Button>
            </div>

            <div style={{ display: current === 3 ? 'block' : 'none', maxWidth: 700, margin: '0 auto' }}>
              <Descriptions bordered column={1} size="small" className="soft-panel">
                <Descriptions.Item label="版本类型">
                  {versionTypeNames[form.getFieldValue('version_type')] || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="产品">
                  {isMarketVersion ? '市场版本' : (products.find(p => p.id === form.getFieldValue('product_id'))?.model || '-')}
                </Descriptions.Item>
                {isMarketVersion && (
                  <Descriptions.Item label="市场分类">
                    {form.getFieldValue('market_category') || '未分类'}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="版本号">
                  <Text strong style={{ fontFamily: 'monospace' }}>v{form.getFieldValue('version') || '-'}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="发布类型">
                  {releaseTypeNames[form.getFieldValue('release_type')] || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="上传者">{form.getFieldValue('uploaded_by') || '-'}</Descriptions.Item>
                <Descriptions.Item label="描述">{form.getFieldValue('description') || '-'}</Descriptions.Item>
                <Descriptions.Item label="文件">
                  {fileList.length > 0 ? fileList[0].name : '未上传'}
                </Descriptions.Item>
                <Descriptions.Item label="功能记录">{features.filter(f => f.title.trim()).length} 项</Descriptions.Item>
              </Descriptions>

              {features.filter(f => f.title.trim()).length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <Text strong style={{ marginBottom: 8, display: 'block' }}>功能记录预览：</Text>
                  {features.filter(f => f.title.trim()).map((f, i) => (
                    <div key={i} style={{ padding: '8px 12px', background: '#fafafa', marginBottom: 4, borderRadius: 4 }}>
                      <Text strong>{f.title}</Text>
                      <Text type="secondary" style={{ marginLeft: 8 }}>
                        [{{ new_feature: '新功能', improvement: '改进', bugfix: '修复' }[f.type]}]
                      </Text>
                      {f.description && <div><Text type="secondary" style={{ fontSize: 12 }}>{f.description}</Text></div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <Divider />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button disabled={current === 0} onClick={() => setCurrent(current - 1)}>
              上一步
            </Button>
            <Space>
              <Button onClick={() => navigate('/versions')}>取消</Button>
              {current < 3 ? (
                <Button type="primary" onClick={goNext}>
                  下一步
                </Button>
              ) : (
                <Button type="primary" icon={<RocketOutlined />} loading={submitting} onClick={handleSubmit}>
                  提交版本
                </Button>
              )}
            </Space>
          </div>
        </Card>
      </Form>
    </div>
  );
}
